import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  ISectionProps,
  IPatientMedicationsResponse,
} from "../../../../@types/Patient";
import { ChevronDown } from "../../../../assets/icons/ChevronDown";
import { ChevronUp } from "../../../../assets/icons/ChevronUp";
import { CustomIconButton } from "../../../../components/CustomIconButton";
import { useInfiniteMedications } from "../../../../hooks/queries/medications";
import { usePatientWards } from "../../../../hooks/queries/patients";
import useIsMobile from "../../../../hooks/useIsMobile";
import { useModal } from "../../../../hooks/useModal";
import { PageTitle } from "../../../../layout/components/PageTitle";
import { formatTimesOfDay } from "../../../../utils/dateFormatter";
import { MedicationAdherenceModal } from "../MedicationAdherenceModal";
import { PatientMedicationsTable } from "../PatientMedicationsTable";
import { NewDataCOntainer, TitleContainer } from "./MedicationSection.styles";

export function MedicationsSection({
  patient,
  updateColapsedSections,
  collapsedSection,
}: ISectionProps) {
  const { t } = useTranslation();

  const { isMobile } = useIsMobile();
  const { openModal } = useModal();
  const highlightedRows = [false];

  const [showNewData, setShowNewData] = useState<boolean>(false);

  const { patientWards } = usePatientWards(patient?.id);

  // EN-5078: patients in these wards manage their own medications in the app —
  // the table swaps the acceptance/dosage columns for the schedule Times, and
  // the adherence modal shows each dose's clock time instead of Dosage/Route.
  // The flag is computed server-side over the PATIENT's full ward set
  // (any-ward-wins), because the login-cached ward configs only cover the
  // wards the VIEWER has access to.
  const isPatientAddMedicationEnabled =
    patientWards?.enablePatientAddMedication ?? false;

  const {
    medications: patientMedicationTable,
    isError,
    isFetching,
    refetch,
    isRefetching,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteMedications(patient.id);

  const handleRefreshTable = () => {
    refetch();
  };

  const parsePatientMedicationsToTable: IPatientMedicationsResponse[] =
    useMemo(() => {
      if (!patientMedicationTable) return [];

      const handleEmptyTableValues = (valueToCheck: string) => {
        if (valueToCheck && valueToCheck !== "") {
          return valueToCheck;
        }
        return "-";
      };

      return patientMedicationTable.map(
        (patientMedication: IPatientMedicationsResponse) => {
          const removedStatus =
            patientMedication.status !== "Accepted" &&
            patientMedication.archived;

          const removedLabel = removedStatus
            ? ` ${t("patientPage_medication_removed")}`
            : "";

          return {
            id: patientMedication.id,
            name: `${handleEmptyTableValues(
              patientMedication.name
            )}${removedLabel}`,
            frequency: handleEmptyTableValues(patientMedication.frequency),
            times: formatTimesOfDay(
              Array.isArray(patientMedication.times)
                ? patientMedication.times
                : undefined
            ),
            source: handleEmptyTableValues(patientMedication.source),
            dosage: handleEmptyTableValues(patientMedication.dosage),
            status: handleEmptyTableValues(patientMedication.status),
            route: handleEmptyTableValues(patientMedication.route),
          };
        }
      );
    }, [patientMedicationTable, t]);

  useEffect(() => {
    if (
      collapsedSection.value === true &&
      !isFetching &&
      !isRefetching &&
      patientMedicationTable &&
      patientMedicationTable.length > 0
    ) {
      // Medication rows carry no created timestamp (the API's old `time` field
      // was never populated and is gone), so the far-past fallback keeps this
      // check inert — exactly the behavior it always had.
      const mostRecentMedicationDate = dayjs("2000-01-01");
      const collapsedDate = dayjs(collapsedSection.collapsedDate);

      if (mostRecentMedicationDate > collapsedDate) {
        setShowNewData(true);
      }
    }
  }, [patientMedicationTable]);

  // Row click opens the "marked as taken" adherence history for that medication.
  const handleRowClick = (row: IPatientMedicationsResponse) => {
    openModal(
      <MedicationAdherenceModal
        patientMedicationId={String(row.id)}
        medicationName={row.name}
        patientAddMedication={isPatientAddMedicationEnabled}
      />,
      {
        width: "1180px",
        maxWidth: "96vw",
        height: "676px",
        showCloseButton: true,
        padding: "12px 0 0 24px",
      }
    );
  };

  const handleCollapseSection = () => {
    if (collapsedSection.value === true) {
      setShowNewData(false);
    }
    updateColapsedSections("medicationsSection");
  };

  return (
    <>
      {!isMobile && (
        <TitleContainer>
          <PageTitle>{t("patientPage_sections_medication")}</PageTitle>
          {showNewData && (
            <NewDataCOntainer>
              {t("patientPage_sections_newData")}
            </NewDataCOntainer>
          )}
          <CustomIconButton
            style={{ marginLeft: "2px" }}
            onClick={handleCollapseSection}
          >
            {collapsedSection.value ? <ChevronUp /> : <ChevronDown />}
          </CustomIconButton>
        </TitleContainer>
      )}
      <div
        style={{
          height: collapsedSection.value ? "0" : "",
          overflow: collapsedSection.value ? "hidden" : "",
        }}
      >
        <PatientMedicationsTable
          isLoading={isFetching && !isRefetching && !isFetchingNextPage}
          isError={isError}
          content={parsePatientMedicationsToTable}
          hideAcceptedAndDosage={isPatientAddMedicationEnabled}
          highlightedRows={highlightedRows}
          refreshTable={handleRefreshTable}
          onRowClick={handleRowClick}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          fetchNextPage={fetchNextPage}
          useInfiniteScroll
        />
      </div>
    </>
  );
}
